import React, { useEffect, useState } from "react";
import {
    Container,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Box,
    AppBar,
    Toolbar,
    CircularProgress,
    Card,
    FormControlLabel,
    Checkbox,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Link,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
} from "@mui/material";
import axios from "axios";
import lifeLeafLogo from "../assets/logo.png";
import { URL } from "../configs/config";
import withToast from "../hocs/withToast";
import { CheckCircle, FiberManualRecord as BulletIcon } from "@mui/icons-material";
import { GetApiKeyCall } from "../api/helper";

const API_BASE_URL = URL.LOCAL.getApiKey;
const GRAPHQL_URL = URL.LOCAL.graphQlAdmin;

const AcceptCareTeamComponent = ({ notify }) => {
    const [careTeam, setCareTeam] = useState([]);
    const [patientId, setPatientId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showSuccessPage, setShowSuccessPage] = useState(false);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [termsDialogOpen, setTermsDialogOpen] = useState(false);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const encoded = params.get("data");

        if (encoded) {
            try {
                const decoded = atob(decodeURIComponent(encoded));
                const parsed = JSON.parse(decoded);
                const { careTeam, patientId } = parsed;
                setCareTeam(careTeam);
                setPatientId(patientId);
            } catch (err) {
                console.error("Failed to decode and parse data:", err);
            }
        }
    }, []);

    useEffect(() => {
        if (showSuccessPage) {
            window.history.replaceState(null, '', '/acceptCareTeam');
        }
    }, [showSuccessPage]);

    const handleAcceptAll = async () => {
        if (!patientId || careTeam.length === 0)
            return notify("No care team found to accept.", "warning");

        if (!termsAccepted)
            return notify("Please accept the terms and conditions first.", "warning");

        setLoading(true);

        const getApiKey = await GetApiKeyCall();

        try {
            const query = `mutation acceptCareTeam($patientId: Int) {
                acceptCareTeamForPatient(patientId: $patientId) {
                    statusCode
                    body
                }
            }`;

            const variables = { patientId };

            const graphqlRes = await axios.post(
                GRAPHQL_URL,
                {
                    query,
                    variables,
                    operationName: "acceptCareTeam"
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        "x-api-key": getApiKey?.data?.apiKeyGPL,
                        "access-method": "WEB"
                    }
                }
            );

            const result = graphqlRes.data.data.acceptCareTeamForPatient;
            const message = JSON.parse(result.body)?.message || "Care team accepted.";
            notify(message, "success");
            setShowSuccessPage(true);
        } catch (error) {
            console.error("Error:", error);
            notify("Something went wrong while accepting the care team.", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleOpenTerms = () => {
        setTermsDialogOpen(true);
    };

    const handleCloseTerms = () => {
        setTermsDialogOpen(false);
    };

    if (showSuccessPage) {
        return (
            <>
                <AppBar position="static" sx={{ backgroundColor: "white", boxShadow: "none" }}>
                    <Toolbar>
                        <Box display="flex" alignItems="center" justifyContent="center" sx={{ flexGrow: 1 }}>
                            <img src={lifeLeafLogo} width={40} height={40} alt="LifeLeaf Logo" />
                            <Typography variant="h6" sx={{ fontWeight: "bold", ml: 1, color: "black" }}>
                                LIFELEAF - Remote Monitoring
                            </Typography>
                        </Box>
                    </Toolbar>
                </AppBar>
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="calc(100dvh - 10dvh)"
                    bgcolor="#f0fdf4"
                >

                    <Card sx={{ p: 4, borderRadius: 4, textAlign: "center", maxWidth: 400 }}>
                        <CheckCircle sx={{ fontSize: 60, color: "green", mb: 2 }} />
                        <Typography variant="h5" fontWeight="bold" color="green">
                            Success!
                        </Typography>
                        <Typography variant="body1" mt={1}>
                            The care team has been successfully accepted.
                        </Typography>
                    </Card>
                </Box>
            </>
        );
    }

    return (
        <Container maxWidth="md" sx={{ mt: 4 }}>
            {/* Header */}
            <AppBar position="static" elevation={0} sx={{ bgcolor: "white", mb: 3 }}>
                <Toolbar>
                    <img src={lifeLeafLogo} alt="LifeLeaf" width={40} height={40} />
                    <Typography variant="h6" sx={{ ml: 2, fontWeight: "bold", color: "#1e1e1e" }}>
                        LIFELEAF - Remote Monitoring
                    </Typography>
                </Toolbar>
            </AppBar>

            <Card variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
                <Typography variant="subtitle1" mb={2}>
                    The following care team members are requesting access to your health details:
                </Typography>

                <TableContainer component={Paper} variant="outlined">
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>Name</strong></TableCell>
                                <TableCell><strong>Email</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {careTeam.map((member, index) => (
                                <TableRow key={index}>
                                    <TableCell>{member.user}</TableCell>
                                    <TableCell>{member.email}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Box mt={3} p={2} bgcolor="#f8f9fa" borderRadius={1}>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        By accepting, you will grant these healthcare professionals access to your Protected Health Information (PHI) and Personal Identifiable Information (PII) for care purposes.
                    </Typography>

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={termsAccepted}
                                onChange={(e) => setTermsAccepted(e.target.checked)}
                                color="primary"
                            />
                        }
                        label={
                            <Typography variant="body2">
                                I agree to the <Link component="button" onClick={handleOpenTerms} underline="hover">Terms and Conditions</Link> regarding my health data access
                            </Typography>
                        }
                    />
                </Box>

                <Box display="flex" justifyContent="flex-end" mt={3}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleAcceptAll}
                        disabled={loading || !termsAccepted}
                        size="large"
                        sx={{ minWidth: 140 }}
                    >
                        {loading ? <CircularProgress size={24} color="inherit" /> : "Accept"}
                    </Button>
                </Box>
            </Card>

            {/* Terms and Conditions Dialog */}
            <Dialog
                open={termsDialogOpen}
                onClose={handleCloseTerms}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle sx={{ fontWeight: "bold", borderBottom: "1px solid #e0e0e0" }}>
                    Terms and Conditions for Health Data Access
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <Typography variant="h6" gutterBottom>
                        Personal Identifiable Information (PII)
                    </Typography>
                    <List>
                        <ListItem>
                            <ListItemIcon sx={{ minWidth: '30px' }}>
                                <BulletIcon sx={{ fontSize: 10 }} />
                            </ListItemIcon>
                            <ListItemText primary="Your PII includes information that can be used to identify you, such as your name, address, date of birth, and contact details." />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon sx={{ minWidth: '30px' }}>
                                <BulletIcon sx={{ fontSize: 10 }} />
                            </ListItemIcon>
                            <ListItemText primary="Access to this information will be limited to authorized members of your Care Team." />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon sx={{ minWidth: '30px' }}>
                                <BulletIcon sx={{ fontSize: 10 }} />
                            </ListItemIcon>
                            <ListItemText primary="This data is secured with industry-standard encryption and security protocols." />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon sx={{ minWidth: '30px' }}>
                                <BulletIcon sx={{ fontSize: 10 }} />
                            </ListItemIcon>
                            <ListItemText primary="We maintain detailed access logs of who has viewed your personal information." />
                        </ListItem>
                    </List>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="h6" gutterBottom>
                        Protected Health Information (PHI)
                    </Typography>
                    <List>
                        <ListItem>
                            <ListItemIcon sx={{ minWidth: '30px' }}>
                                <BulletIcon sx={{ fontSize: 10 }} />
                            </ListItemIcon>
                            <ListItemText primary="Your PHI includes your medical records, health status, treatment plans, and other health-related information." />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon sx={{ minWidth: '30px' }}>
                                <BulletIcon sx={{ fontSize: 10 }} />
                            </ListItemIcon>
                            <ListItemText primary="This information is protected under HIPAA regulations and will only be used for providing you with healthcare services." />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon sx={{ minWidth: '30px' }}>
                                <BulletIcon sx={{ fontSize: 10 }} />
                            </ListItemIcon>
                            <ListItemText primary="Access is restricted to healthcare professionals directly involved in your care." />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon sx={{ minWidth: '30px' }}>
                                <BulletIcon sx={{ fontSize: 10 }} />
                            </ListItemIcon>
                            <ListItemText primary="Your health data will never be shared with third parties for marketing or advertising purposes." />
                        </ListItem>
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseTerms} color="primary" variant="contained">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default withToast(AcceptCareTeamComponent);